/**
 * StatusPill — canonical status color config and reusable pill component.
 *
 * Single source of truth for all entity status badge/pill styling throughout the app.
 * Covers: user status, medication status, patient status, auth status, etc.
 *
 * Usage (component):
 *   <StatusPill status="Active" />
 *   <StatusPill status="DISCONTINUED" className="opacity-60" />
 *
 * Usage (utilities):
 *   getStatusConfig('active')          → full config object
 *   getStatusPillClassName('expired')  → "bg-red-600 border-red-700 text-white font-semibold text-xs rounded border"
 *
 * Color scheme:
 *   Active        → emerald-600
 *   Inactive      → slate-500
 *   Expired       → red-600
 *   Pending       → amber-600
 *   Cancelled     → stone-500
 *   On Hold       → amber-600
 *   Discontinued  → slate-800
 */

import React from 'react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface StatusConfig {
  /** Display label (e.g. "Active", "On Hold") */
  label: string;
  /** Full Tailwind class string for the pill element */
  className: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

const BASE = 'font-semibold text-xs rounded border';

const STATUS_MAP: Record<string, StatusConfig> = {
  // ── Active ──────────────────────────────────────────────────────────────────
  ACTIVE: {
    label: 'Active',
    className: `bg-emerald-600 border-emerald-700 text-white ${BASE}`,
  },

  // ── Inactive ────────────────────────────────────────────────────────────────
  INACTIVE: {
    label: 'Inactive',
    className: `bg-slate-500 border-slate-600 text-white ${BASE}`,
  },

  // ── Expired ─────────────────────────────────────────────────────────────────
  EXPIRED: {
    label: 'Expired',
    className: `bg-red-600 border-red-700 text-white ${BASE}`,
  },

  // ── Pending ─────────────────────────────────────────────────────────────────
  PENDING: {
    label: 'Pending',
    className: `bg-amber-600 border-amber-700 text-white ${BASE}`,
  },

  // ── Pending admission (Phase 6: referral shell, not yet admitted) ─────────
  PENDING_ADMISSION: {
    label: 'Pending',
    className: `bg-sky-600 border-sky-700 text-white ${BASE}`,
  },

  // ── Cancelled ───────────────────────────────────────────────────────────────
  CANCELLED: {
    label: 'Cancelled',
    className: `bg-stone-500 border-stone-600 text-white ${BASE}`,
  },

  // ── On Hold ─────────────────────────────────────────────────────────────────
  ON_HOLD: {
    label: 'On Hold',
    className: `bg-amber-600 border-amber-700 text-white ${BASE}`,
  },
  // Alias: "ON HOLD" → same as ON_HOLD
  'ON HOLD': {
    label: 'On Hold',
    className: `bg-amber-600 border-amber-700 text-white ${BASE}`,
  },
  ONHOLD: {
    label: 'On Hold',
    className: `bg-amber-600 border-amber-700 text-white ${BASE}`,
  },

  // ── Discontinued ────────────────────────────────────────────────────────────
  DISCONTINUED: {
    label: 'Discontinued',
    className: `bg-slate-800 border-slate-900 text-white ${BASE}`,
  },

  // ── Terminated (alias → Cancelled style) ────────────────────────────────────
  TERMINATED: {
    label: 'Terminated',
    className: `bg-stone-500 border-stone-600 text-white ${BASE}`,
  },
};

const DEFAULT_CONFIG: StatusConfig = {
  label: '',
  className: `bg-slate-500 border-slate-600 text-white ${BASE}`,
};

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for a status string.
 * Normalises to uppercase with underscores; falls back to a slate default.
 */
export function getStatusConfig(status: string): StatusConfig {
  if (!status) return { ...DEFAULT_CONFIG, label: status };
  const key = status.trim().toUpperCase().replace(/\s+/g, '_');
  return STATUS_MAP[key] ?? { ...DEFAULT_CONFIG, label: status };
}

/**
 * Returns the full Tailwind class string for a status pill.
 *
 * @example
 *   className={`px-2 py-0.5 ${getStatusPillClassName('Active')}`}
 */
export function getStatusPillClassName(status: string): string {
  return getStatusConfig(status).className;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface StatusPillProps {
  /** Status string: Active | Inactive | Expired | Pending | Cancelled | On Hold | Discontinued … */
  status: string;
  /** Override display label (defaults to canonical label from config) */
  label?: string;
  /** Extra Tailwind classes appended to the root element */
  className?: string;
}

/**
 * StatusPill — canonical status badge component.
 *
 * Renders as: font-semibold · text-xs · rounded · border + status-specific
 * solid background, matching border, and white text.
 */
export function StatusPill({ status, label, className = '' }: StatusPillProps) {
  const config = getStatusConfig(status);
  const displayLabel = label ?? config.label;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 ${config.className} ${className}`.trim()}>
      {displayLabel}
    </span>
  );
}

export default StatusPill;
